// src/components/SellerPortal.jsx
// Seller-facing document upload portal

import React, { useState, useRef } from 'react';
import { 
  Briefcase, ArrowLeft, Clock, AlertCircle, CheckSquare, 
  Upload, FileText, Loader2 
} from 'lucide-react';
import { useDeal } from '../hooks/useSupabase';
import { useDisplayColors } from '../lib/ThemeContext';

// LFG Brand Colors (defaults)
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Google Drive logo URL
const LFG_LOGO_URL = 'https://hyqjtpsiwbumowpljwyc.supabase.co/storage/v1/object/public/public-assets/LFGLogo.png';

const PHASES = [
  { id: 'future-target', name: 'Future Target' },
  { id: 'prospect',      name: 'Prospect' },
  { id: 'diligence',     name: 'Diligence' },
  { id: 'offer-made',    name: 'Offer Made' },
  { id: 'under-loi',     name: 'Under LOI' },
  { id: 'on-hold',       name: 'On Hold' },
  { id: 'closed',        name: 'Closed' },
  { id: 'canceled',      name: 'Canceled' },
];

export default function SellerPortal({ dealId, organization, pc, setView, setSellerDealId, showToast }) {
  const { deal, tasks, loading, uploadFile } = useDeal(dealId);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  const getPhaseName = (phaseId) => PHASES.find(p => p.id === phaseId)?.name || phaseId;

  // Use organization colors or fall back to LFG defaults
  const primaryColor = pc || organization?.primary_color || LFG_COLORS.navy;
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, null);

  const handleFileUpload = async (taskId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [taskId]: true }));
    try {
      await uploadFile(taskId, file, { name: 'Seller', type: 'seller' });
      showToast('Document uploaded successfully');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUploading(prev => ({ ...prev, [taskId]: false }));
      if (fileInputRefs.current[taskId]) {
        fileInputRefs.current[taskId].value = '';
      }
    }
  };

  if (loading || !deal) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading portal...</p>
      </div>
    );
  }

  const requestedTasks = tasks.filter(t => 
    t.is_doc_request && (t.status === 'Requested' || t.status === 'Received')
  );
  const pendingCount = requestedTasks.filter(t => t.status === 'Requested').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={organization?.logo_url || LFG_LOGO_URL} 
                alt="Logo" 
                className="h-8 w-auto max-w-[100px] object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center justify-center w-8 h-8 rounded" style={{ backgroundColor: `${primaryColor}15` }}>
                <Briefcase className="w-5 h-5" style={{ color: displayPrimary }} />
              </div>
              <div>
                <div className="font-semibold" style={{ color: displayPrimary }}>
                  {organization?.name || 'Landmark Legacy Room'}
                </div>
                <div className="text-sm text-gray-500">Document Upload Portal</div>
              </div>
            </div>
            <button 
              onClick={() => { setView('portfolio'); setSellerDealId(null); }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Exit Portal
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h1 className="text-2xl font-bold mb-2" style={{ color: displayNavy }}>{deal.name}</h1>
          <p className="text-gray-600">
            Please upload the requested documents below. Click the upload button next to each item to add files.
          </p>
          {pendingCount > 0 && (
            <div className="mt-4 flex items-center gap-2 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>
                <strong>{pendingCount}</strong> document{pendingCount !== 1 ? 's' : ''} still needed
              </span>
            </div>
          )}
        </div>

        {requestedTasks.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
            <CheckSquare className="w-12 h-12 mx-auto mb-3" style={{ color: displayGreen }} />
            <div className="text-lg font-medium text-gray-700">All caught up!</div>
            <div>No documents are currently requested.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {requestedTasks.map(task => (
              <div key={task.id} className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        task.status === 'Requested' 
                          ? 'bg-yellow-100 text-yellow-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {task.status === 'Requested' ? '● Needed' : '✓ Received'}
                      </span>
                      <span className="text-xs text-gray-400">{getPhaseName(task.phase)}</span>
                    </div>
                    <div className="font-medium">{task.name}</div>
                    {task.due_date && (
                      <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" /> Due: {task.due_date}
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      ref={el => fileInputRefs.current[task.id] = el}
                      type="file"
                      onChange={(e) => handleFileUpload(task.id, e)}
                      className="hidden"
                      id={`seller-file-${task.id}`}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt"
                    />
                    <label
                      htmlFor={`seller-file-${task.id}`}
                      className={`px-4 py-2 rounded text-white flex items-center gap-2 hover:opacity-90 cursor-pointer transition-opacity ${
                        uploading[task.id] ? 'opacity-50 cursor-wait' : ''
                      }`}
                      style={{ backgroundColor: primaryColor }}
                    >
                      {uploading[task.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Upload
                    </label>
                  </div>
                </div>

                {task.files?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-gray-500 mb-2 font-medium">Uploaded files:</div>
                    <div className="space-y-1">
                      {task.files.map(f => (
                        <div key={f.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded p-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="flex-1">{f.original_name}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(f.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
